import React, { useRef, useCallback, useState } from 'react';
import { getBlockDef } from './BlockRenderer';
import { GRID_COLS, GRID_ROWS } from './layoutUtils';

/**
 * Overlay rendered on top of each block in layout-edit mode.
 * Provides drag-to-move, drag-to-resize from all edges/corners,
 * autofit, and remove — all operating directly on the A4 preview canvas.
 */
const InlineBlockOverlay = ({
  block,        // { id, x, y, w, h }
  pageIndex,
  canvasEl,     // the .qr-content-canvas DOM element
  blockEl,      // the .qr-block-positioned DOM element (for autofit)
  onBlockChange,
  onRemoveBlock,
  onReorderBlock,
  isOverflowing,
  customBlocks,
  onBlockDefChange,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const dragState = useRef(null);

  const def = getBlockDef(block.id, customBlocks);
  const typeLabel = def?.type === 'text' ? 'Aa'
    : def?.type === 'data' ? '#'
    : def?.type === 'image' ? 'Img'
    : def?.type === 'shape' ? '◇'
    : def?.type === 'brandname' ? 'Br'
    : '*';

  // Convert pixel delta to grid units using the actual canvas dimensions
  const pxToGrid = useCallback(() => {
    if (!canvasEl) return { colW: 1, rowH: 1 };
    const rect = canvasEl.getBoundingClientRect();
    return {
      colW: rect.width / GRID_COLS,
      rowH: rect.height / GRID_ROWS,
    };
  }, [canvasEl]);

  // ── Drag to move ──
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const { colW, rowH } = pxToGrid();
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: block.x,
      origY: block.y,
      colW,
      rowH,
    };
    setIsDragging(true);

    const handleMouseMove = (e) => {
      const s = dragState.current;
      if (!s) return;
      const dx = Math.round((e.clientX - s.startX) / s.colW);
      const dy = Math.round((e.clientY - s.startY) / s.rowH);
      const newX = Math.max(0, Math.min(GRID_COLS - block.w, s.origX + dx));
      const newY = Math.max(0, s.origY + dy);
      onBlockChange({ ...block, x: newX, y: newY });
    };

    const handleMouseUp = () => {
      dragState.current = null;
      setIsDragging(false);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [block, pxToGrid, onBlockChange]);

  // ── Drag to resize from any edge/corner ──
  // edges: 'n','s','e','w','ne','nw','se','sw'
  const handleResizeStart = useCallback((edge) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { colW, rowH } = pxToGrid();
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: block.x,
      origY: block.y,
      origW: block.w,
      origH: block.h,
      colW,
      rowH,
      edge,
    };
    setIsResizing(true);

    const handleMouseMove = (e) => {
      const s = dragState.current;
      if (!s) return;
      const dx = Math.round((e.clientX - s.startX) / s.colW);
      const dy = Math.round((e.clientY - s.startY) / s.rowH);

      let { origX: newX, origY: newY, origW: newW, origH: newH } = s;
      const dir = s.edge;

      // East side — expand/shrink width
      if (dir.includes('e')) {
        newW = Math.max(1, Math.min(GRID_COLS - newX, s.origW + dx));
      }
      // West side — move x and shrink width
      if (dir.includes('w')) {
        const moved = Math.min(dx, s.origW - 1); // can't shrink below 1
        newX = Math.max(0, s.origX + moved);
        newW = s.origW - (newX - s.origX);
      }
      // South side — expand/shrink height
      if (dir.includes('s')) {
        newH = Math.max(1, s.origH + dy);
      }
      // North side — move y and shrink height
      if (dir === 'n' || dir === 'ne' || dir === 'nw') {
        const moved = Math.min(dy, s.origH - 1);
        newY = Math.max(0, s.origY + moved);
        newH = s.origH - (newY - s.origY);
      }

      onBlockChange({ ...block, x: newX, y: newY, w: newW, h: newH });
    };

    const handleMouseUp = () => {
      dragState.current = null;
      setIsResizing(false);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [block, pxToGrid, onBlockChange]);

  // ── Autofit: temporarily unconstrain the block height to measure natural content size ──
  const handleAutofit = useCallback(() => {
    if (!blockEl || !canvasEl) return;
    const canvasH = canvasEl.clientHeight;
    if (canvasH <= 0) return;

    const origHeight = blockEl.style.height;
    const origOverflow = blockEl.style.overflow;
    blockEl.style.height = 'auto';
    blockEl.style.overflow = 'hidden';

    const contentH = blockEl.scrollHeight;

    blockEl.style.height = origHeight;
    blockEl.style.overflow = origOverflow;

    const neededH = Math.min(GRID_ROWS, Math.max(1, Math.ceil(contentH / canvasH * GRID_ROWS)));
    if (neededH !== block.h) {
      onBlockChange({ ...block, h: neededH });
    }
  }, [block, blockEl, canvasEl, onBlockChange]);

  return (
    <div
      className={`inline-overlay ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''} ${isOverflowing ? 'overflow' : ''}`}
    >
      {/* Top bar: drag handle + label + controls */}
      <div className="inline-overlay-bar" onMouseDown={handleDragStart}>
        <span className="inline-overlay-handle">&#x2801;&#x2801;</span>
        <span className={`block-type-badge block-type-${def?.type || 'special'}`}>{typeLabel}</span>
        <span className="inline-overlay-label">{def?.label || block.id}</span>
        <div className="inline-overlay-actions" onMouseDown={e => e.stopPropagation()}>
          {isOverflowing && (
            <span className="inline-overflow-badge">OVERFLOW</span>
          )}
          {def?.type === 'text' && onBlockDefChange && (() => {
            const blockDef = customBlocks?.[block.id];
            if (!blockDef) return null;
            const bgColor = blockDef.bgColor || null;
            return (
              <span className="overlay-bg-picker">
                <input
                  type="color"
                  value={bgColor || '#f5f5f5'}
                  onChange={(e) => onBlockDefChange(block.id, { ...blockDef, bgColor: e.target.value })}
                  className="overlay-bg-input"
                  title="Background colour"
                />
                {bgColor && (
                  <button
                    className="overlay-bg-clear"
                    onClick={() => onBlockDefChange(block.id, { ...blockDef, bgColor: null })}
                    title="Remove background"
                  >
                    ✕
                  </button>
                )}
              </span>
            );
          })()}
          <button
            className="inline-overlay-btn inline-fit-btn"
            onClick={handleAutofit}
            title="Auto-resize height to fit content"
          >
            Fit
          </button>
          <button
            className={`inline-overlay-btn${block.fullBleed ? ' active' : ''}`}
            onClick={() => onBlockChange({ ...block, fullBleed: !block.fullBleed })}
            title="Full bleed — extend to the page edge on any flush side"
          >
            Bleed
          </button>
          {onReorderBlock && (
            <>
              <button
                className="inline-overlay-btn"
                onClick={() => onReorderBlock(false)}
                title="Send to back"
              >
                Back
              </button>
              <button
                className="inline-overlay-btn"
                onClick={() => onReorderBlock(true)}
                title="Bring to front"
              >
                Front
              </button>
            </>
          )}
          <button
            className="inline-overlay-btn inline-remove-btn"
            onClick={() => onRemoveBlock(block.id)}
            title="Remove block"
          >
            &times;
          </button>
        </div>
      </div>

      {/* Resize handles — all 4 edges + 4 corners */}
      <div className="inline-resize-handle resize-n" onMouseDown={handleResizeStart('n')} />
      <div className="inline-resize-handle resize-s" onMouseDown={handleResizeStart('s')} />
      <div className="inline-resize-handle resize-e" onMouseDown={handleResizeStart('e')} />
      <div className="inline-resize-handle resize-w" onMouseDown={handleResizeStart('w')} />
      <div className="inline-resize-handle resize-nw" onMouseDown={handleResizeStart('nw')} />
      <div className="inline-resize-handle resize-ne" onMouseDown={handleResizeStart('ne')} />
      <div className="inline-resize-handle resize-sw" onMouseDown={handleResizeStart('sw')} />
      <div className="inline-resize-handle resize-se" onMouseDown={handleResizeStart('se')} />
    </div>
  );
};

export default InlineBlockOverlay;
